import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errText } from "@/lib/api";
import { firebaseReady, googleErrorText, googleIdToken } from "@/lib/firebase";
import { trackEvent } from "@/lib/gtag";
import { useSeo } from "@/lib/useSeo";
import { useAuth } from "@/context/AuthContext";

const HERO =
  "https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBob3VzZSUyMGV4dGVyaW9yfGVufDB8fHx8MTc4Njc0MDA2N3ww&ixlib=rb-4.1.0&q=85";

export default function Signup() {
  const [step, setStep] = useState("details");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  useSeo({ title: "Create your account", description: "Sign up on Houses7.com to shortlist homes, get instant alerts and list your own property.", noindex: true });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const sendOtp = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      await api.post("/auth/signup/send-otp", {
        name: form.name,
        email: form.email.trim(),
        password: form.password,
        phone: form.phone || null,
      });
      setStep("otp");
      toast.success(`Verification code sent to ${form.email.trim()}`);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (code.trim().length < 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/signup/verify-otp", { email: form.email.trim(), code: code.trim() });
      setUser(data);
      toast.success("Account created");
      trackEvent("sign_up", { method: "email_otp" });
      navigate(data.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const googleSignup = async () => {
    if (!firebaseReady) return toast.error("Google sign-in is not configured yet");
    setBusy(true);
    try {
      const idToken = await googleIdToken();
      const { data } = await api.post("/auth/firebase", { id_token: idToken });
      setUser(data);
      toast.success("Welcome to Houses7.com");
      trackEvent("sign_up", { method: "google" });
      navigate(data.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(err?.code ? googleErrorText(err) : errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-10 grid gap-12 lg:grid-cols-[1fr_420px] items-start">
      <div className="relative hidden lg:block overflow-hidden rounded-3xl">
        <img src={HERO} alt="Modern home" className="h-[560px] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="absolute bottom-0 p-10">
          <h2 className="font-display text-4xl text-white leading-tight">
            Create your account
            <br />
            in under a minute.
          </h2>
          <p className="mt-3 max-w-sm text-sm text-white/80">
            Shortlist homes, get instant alerts and list your own property.
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-border p-6 md:p-8 shadow-panel" data-testid="signup-card">
        <h1 className="font-display text-2xl">Create your account</h1>
        <p className="mt-1.5 text-[13px] text-muted-foreground">
          {step === "details" ? "We'll email you a 6-digit code to confirm it's you." : `Code sent to ${form.email.trim()}`}
        </p>

        {step === "details" ? (
          <>
            <button
              onClick={googleSignup}
              disabled={busy}
              className="mt-6 w-full h-12 rounded-lg bg-foreground text-background text-sm font-bold hover:brightness-110 transition-all duration-200 disabled:opacity-60"
              data-testid="signup-google-btn"
            >
              {busy ? "Please wait…" : "Continue with Google"}
            </button>

            <div className="my-6 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              <span className="h-px flex-1 bg-border" /> or sign up with email <span className="h-px flex-1 bg-border" />
            </div>

            <form onSubmit={sendOtp} className="space-y-3.5" data-testid="signup-form">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Full name</Label>
                <Input value={form.name} onChange={set("name")} required className="h-12 rounded-lg" data-testid="signup-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Email</Label>
                <Input type="email" value={form.email} onChange={set("email")} required className="h-12 rounded-lg" data-testid="signup-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Mobile number (optional)</Label>
                <Input type="tel" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" className="h-12 rounded-lg" data-testid="signup-phone-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Password</Label>
                <Input type="password" value={form.password} onChange={set("password")} required minLength={8} className="h-12 rounded-lg" data-testid="signup-password-input" />
                <p className="text-[11px] text-muted-foreground">Use at least 8 characters.</p>
              </div>
              <Button type="submit" disabled={busy} className="w-full h-12 rounded-lg text-[15px] font-bold hover:brightness-95" data-testid="signup-send-otp-btn">
                {busy ? "Sending code…" : "Send verification code"}
              </Button>
            </form>
          </>
        ) : (
          <form onSubmit={verifyOtp} className="mt-6 space-y-4" data-testid="signup-otp-form">
            <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2.5 text-[12px] text-muted-foreground">
              <MailCheck className="h-4 w-4" /> Check your inbox — the code expires in 10 minutes.
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">6-digit code</Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                placeholder="______"
                className="h-12 rounded-lg tracking-[0.4em] text-center text-lg"
                data-testid="signup-otp-input"
              />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-12 rounded-lg text-[15px] font-bold hover:brightness-95" data-testid="signup-verify-btn">
              {busy ? "Checking…" : "Verify & create account"}
            </Button>
            <div className="flex items-center justify-between text-[12px]">
              <button type="button" className="font-bold underline" onClick={sendOtp} disabled={busy} data-testid="signup-resend-btn">
                Resend code
              </button>
              <button type="button" className="underline text-muted-foreground" onClick={() => setStep("details")} data-testid="signup-edit-details-btn">
                Change details
              </button>
            </div>
          </form>
        )}

        <p className="mt-6 text-[13px] text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="font-bold text-foreground underline" data-testid="signup-to-login-link">
            Log in
          </Link>
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          <Link to="/" className="underline">Back to home</Link>
        </p>
      </div>
    </div>
  );
}
